/* eslint-disable max-len */

import moment from 'moment';
import * as R from 'ramda';
import angular from 'angular';

export default function PerformControlsReviewDialogController(
  $q,
  $rootScope,
  $scope,
  alerts,
  confirmDialog,
  loaderService,
  ssDialogService,
  vmDataService,
) {
  const vm = this;

  vm.activeRiskControlReviewTaskControl = vm.currentControlsReviewTask.riskControls[0];
  vm.processingSave = false;

  vm.handleNavToRiskControl = _handleNavToRiskControl;
  vm.handleInputChange = _handleInputChange;
  vm.getAttachDocumentIds = _getAttachDocumentIds;
  vm.handleAttachDocuments = _handleAttachDocuments;
  vm.handleDetachDocument = _handleDetachDocument;
  vm.handleReturnToSelect = _handleReturnToSelect;
  vm.handlePerformControlsReviewFormSubmit = _handlePerformControlsReviewFormSubmit;
  vm.handleCacheAttachFolders = _handleCacheAttachFolders;
  vm.handleCloseDialog = _handleCloseDialog;

  _activate();

  /** *******************************************************
   * Event Handlers
   */

  function _handleNavToRiskControl($event, riskControlReviewTaskControl) {
    if (!vm.processingSave) {
      _changeRiskControlPanelView(riskControlReviewTaskControl);
    }
  }

  function _handleInputChange($event) { // eslint-disable-line no-unused-vars
    vm.newRiskControlReviewTaskControl.hasUnsavedChanges = true;
    vm.activeRiskControlReviewTaskControl.status = 'pending';
  }

  function _handleAttachDocuments(documentIds) {
    _updateRiskControlReviewTaskControlDocuments(documentIds).then(() => {
      alerts.success('Documents successfully attached to Risk Control.');
      $scope.performControlsReviewForm.$dirty = true;
    });
  }

  function _handleDetachDocument($event, id) {
    _showConfirmDetachDocumentDialog($event, id);
  }

  function _handleReturnToSelect($event) {
    if ($scope.performControlsReviewForm.$dirty) {
      // Prompt to save changes
      _showConfirmRevertRiskControlChangesDialog($event, () => {
        // Now continue to dialog
        _closeRevertRiskControlChangesDialog()
          .then(() => _closeDialog()
            .then(() => _loadControlsForProductReview()
              .then(() => _showSelectRiskControlsForReviewDialog($event))));
      });
    } else {
      // No changes, continue to dialog
      _closeDialog()
        .then(() => _loadControlsForProductReview()
          .then(() => _showSelectRiskControlsForReviewDialog($event)));
    }
  }

  function _loadControlsForProductReview() {
    return vmDataService.getControlsForProductReview(vm.product.productId)
      .then((response) => {
        loaderService.showViewLoader = false;
        const product = response.data.product;

        angular.extend(vm.product, product);
      });
  }

  function _handlePerformControlsReviewFormSubmit() {
    if ($scope.performControlsReviewForm.$valid) {
      switch (vm.submitType) {
        case 'saveProgress':
          _setFormUnsubmitted();
          _saveControlProgress();
          break;
        case 'saveAndComplete':
          _setFormUnsubmitted();
          _saveAndCompleteControl();
          break;
        case 'unlockControl':
          _setFormUnsubmitted();
          _unlockControl();
          break;
        case 'completeReview':
          _markReviewComplete();
          break;
        default:
        // do nothing
      }
    } else {
      $scope.performControlsReviewForm.$submitted = true;
    }
  }

  function _handleCacheAttachFolders(data) {
    vm.cachedFolderList = data.folderList;
    vm.cachedCustomerServices = data.customerServices;
  }

  function _handleCloseDialog() {
    _closeDialog();
  }

  /** *******************************************************
   * Private Functions
   */

  function _activate() {
    _initData();
    vm.newRiskControlReviewTaskControl = angular.extend({}, vm.activeRiskControlReviewTaskControl);
    _initPermissions();
    _markTaskStatus();
    _setDatesForControls();
  }

  function _initData() {
    vm.currentControlsReviewTask.riskControls = R.map(
      (riskControl) => {
        const selectedMultiplierValue = R.find(
          R.propEq('isSelectedSchemeValue', true,),
          riskControl.riskControlScoreMultiplierScheme.riskControlScoreMultiplierValues,
        );
        if (selectedMultiplierValue) {
          riskControl.riskControlScoreMultiplierValueId = selectedMultiplierValue.riskControlScoreMultiplierValueId;
        }
        _setRiskControlStatus(riskControl);
        riskControl.attachedDocumentIds = R.pluck('documentId', riskControl.riskControlReviewTaskControlDocuments);

        return riskControl;
      },
      vm.currentControlsReviewTask.riskControls,
    );
  }

  function _initPermissions() {
    vm.permissions = {
      canAddFolder: true,
      canDeleteFolder: true,
      canAddDocument: true,
      canDeleteDocument: true,
    };
  }

  function _markTaskStatus() {
    vm.currentControlsReviewTask.allItemsCompleted = _checkIsReviewComplete(vm.currentControlsReviewTask.riskControls);
  }

  function _getRemainingDocumentsAndIds(id) {
    const remainingDocuments = R.reject(
      R.propEq('riskControlReviewTaskControlDocumentId', id),
      vm.newRiskControlReviewTaskControl.riskControlReviewTaskControlDocuments,
    );
    const remainingDocumentIds = R.map(document => document.documentId, remainingDocuments);
    return {
      remainingDocuments,
      remainingDocumentIds,
    };
  }

  function _getAttachDocumentIds() {
    if (vm.newRiskControlReviewTaskControl && vm.newRiskControlReviewTaskControl.riskControlReviewTaskControlDocuments) {
      return []; // R.pluck('documentId', R.take(3, vm.newRiskControlReviewTaskControl.documents));
    }

    return [];
  }

  function _handleConfirmDetachDocument($event, id) {
    const { remainingDocumentIds, remainingDocuments } = _getRemainingDocumentsAndIds(id);

    vm.newRiskControlReviewTaskControl.documents = remainingDocuments;
    vm.activeRiskControlReviewTaskControl.documents = remainingDocuments;
    if (remainingDocumentIds && remainingDocumentIds.length > 0) {
      return _updateRiskControlReviewTaskControlDocuments(remainingDocumentIds).then(() => {
        vm.newRiskControlReviewTaskControl.attachedDocumentIds = remainingDocumentIds;
        alerts.success('Document successfully detached from Risk Control.');
        $scope.performControlsReviewForm.$dirty = true;
        _closeDetachDocumentDialog();
      });
    }
    // eslint-disable-next-line max-len
    return vmDataService.deleteRiskControlReviewTaskControlDocuments(id).then(() => {
      vm.newRiskControlReviewTaskControl.riskControlReviewTaskControlDocuments = [];
      vm.newRiskControlReviewTaskControl.attachedDocumentIds = [];
      alerts.success('Document successfully detached from Risk Control.');
      $scope.performControlsReviewForm.$dirty = true;
      _closeDetachDocumentDialog();
    });
  }

  function _updateRiskControlReviewTaskControlDocuments(attachedIds) {
    loaderService.showViewLoader = true;

    const docIds = R.take(3, attachedIds);
    // const docIds = R.pluck('documentId', docs);

    return vmDataService.updateRiskControlReviewTaskControlDocuments(vm.newRiskControlReviewTaskControl.riskControlReviewTaskControlId, docIds,).then((documentResponse) => {
      vm.newRiskControlReviewTaskControl.riskControlReviewTaskControlDocuments = documentResponse.data;

      angular.forEach(vm.currentControlsReviewTask.riskControls, (currentControl) => {
        if (currentControl.riskControlId === vm.newRiskControlReviewTaskControl.riskControlId) {
          currentControl.riskControlReviewTaskControlDocuments = vm.newRiskControlReviewTaskControl.riskControlReviewTaskControlDocuments;
        }
      });

      loaderService.showViewLoader = false;
      // $scope.performControlsReviewForm.$dirty = true;
    }).finally(() => {
      loaderService.showViewLoader = false;
    });
  }
  /*
  function getDocument(documents, documentId) {
    for (let i = 0; i < documents.length; i += 1) {
      if (documents[i].documentId === documentId) {
        return documents[i];
      }
    }
    return null;
  }
  */
  function _setFormUnsubmitted() {
    $scope.performControlsReviewForm.$setPristine();
    $scope.performControlsReviewForm.$setUntouched();
  }

  function _saveControlProgress() {
    vm.processingSave = true;
    loaderService.showViewLoader = true;
    return _saveActiveRiskControl(false)
      .then(() => {
        alerts.success('The Risk Control has been successfully saved.');
        vm.processingSave = false;
      }).catch(() => {
        alerts.success('There was an error saving the progress of the Risk Control.');
        vm.processingSave = false;
      }).finally(() => {
        loaderService.showViewLoader = false;
      });
  }

  function _saveAndCompleteControl() {
    vm.processingSave = true;
    loaderService.showViewLoader = true;
    return _saveActiveRiskControl(true).then(() => {
      vmDataService.markReviewTaskControlComplete(vm.newRiskControlReviewTaskControl.riskControlReviewTaskControlId)
        .then(() => {
          alerts.success('The Risk Control has been successfully saved and marked as completed.');
          vm.newRiskControlReviewTaskControl.status = 'complete';
          angular.extend(vm.activeRiskControlReviewTaskControl, vm.newRiskControlReviewTaskControl);
          _markTaskStatus();
          vm.processingSave = false;
          $rootScope.$broadcast('refreshVMVendor', { vendorId: vm.product.vendorId });
        });
    }).catch(() => {
      alerts.success('There was an error saving and completing the Risk Control.');
      vm.processingSave = false;
    }).finally(() => {
      loaderService.showViewLoader = false;
    });
  }

  function _unlockControl() {
    vm.processingSave = true;
    loaderService.showViewLoader = true;
    return _unlockRiskControl(false)
      .then(() => {
        alerts.success('The Risk Control has been successfully unlocked.');
        vm.processingSave = false;
      }).catch(() => {
        alerts.success('There was an error unlocking the Risk Control.');
        vm.processingSave = false;
      }).finally(() => {
        loaderService.showViewLoader = false;
      });
  }

  function _markReviewComplete() {
    const isComplete = _checkIsReviewComplete(vm.currentControlsReviewTask.riskControls);

    if (!isComplete) {
      alerts.error('Please mark all Risk Controls complete in order to complete the Control Review');
      return Promise.resolve();
    }

    vm.processingSave = true;
    loaderService.showViewLoader = true;
    return vmDataService.markControlReviewTaskComplete(vm.currentControlsReviewTask.riskControlReviewTaskId)
      .then(() => {
        alerts.success(
          'The Risk Control Review Task has been successfully completed. Any Risk Control '
          + 'Review alerts will be triggered based off today\'s date until another Risk Control '
          + 'Review is performed for this product.',
        );
        $rootScope.$broadcast('refreshVMVendor', { vendorId: vm.product.vendorId });
        $rootScope.$broadcast('refreshNotifications');
        vm.processingSave = false;
        return _closeDialog();
      }).catch(() => {
        alerts.error('An error occurred while marking the review as complete.');
        vm.processingSave = false;
      }).finally(() => {
        loaderService.showViewLoader = false;
      });
  }

  function _changeRiskControlPanelView(riskControlReviewTaskControl) {
    if (vm.newRiskControlReviewTaskControl.hasUnsavedChanges) {
      // Set up the data for the transition
      vm.newRiskControlReviewTaskControl.riskControlScoreMultiplierSchemeId = vm.newRiskControlReviewTaskControl
        .riskControlScoreMultiplierScheme.riskControlScoreMultiplierSchemeId;
      vm.newRiskControlReviewTaskControl.status = vm.activeRiskControlReviewTaskControl.status;

      angular.forEach(vm.currentControlsReviewTask.riskControls, (currentControl) => {
        if (currentControl.riskControlId === vm.newRiskControlReviewTaskControl.riskControlId) {
          angular.extend(currentControl, vm.newRiskControlReviewTaskControl);
        }
      });
    }

    vm.activeRiskControlReviewTaskControl = riskControlReviewTaskControl;
    vm.newRiskControlReviewTaskControl = angular.extend({}, vm.activeRiskControlReviewTaskControl);
  }

  function _setRiskControlStatus(riskControl) {
    if (riskControl.isComplete) {
      riskControl.status = 'complete';
    } else if (riskControl.hasUnsavedChanges) {
      riskControl.status = 'pending';
    } else {
      riskControl.status = 'notStarted';
    }
  }

  function _checkIsReviewComplete(riskControls) {
    let isIncomplete = R.any(R.propEq('isComplete', false), riskControls);

    if (!isIncomplete) {
      isIncomplete = R.any(R.propEq('isComplete', null), riskControls);
    }

    return !isIncomplete;
  }

  function _saveActiveRiskControl(isComplete) {
    vm.newRiskControlReviewTaskControl.riskControlScoreMultiplierSchemeId = vm.newRiskControlReviewTaskControl
      .riskControlScoreMultiplierScheme.riskControlScoreMultiplierSchemeId;
    vm.newRiskControlReviewTaskControl.answersToSave = _assembleAnswers();

    if (vm.newRiskControlReviewTaskControl.socPeriodStartDateMoment !== undefined) {
      vm.newRiskControlReviewTaskControl.socPeriodStartDate = moment(vm.newRiskControlReviewTaskControl.socPeriodStartDateMoment).format('MM/DD/YYYY');
    } else {
      vm.newRiskControlReviewTaskControl.socPeriodStartDate = null;
    }

    if (vm.newRiskControlReviewTaskControl.socPeriodEndDateMoment !== undefined) {
      vm.newRiskControlReviewTaskControl.socPeriodEndDate = moment(vm.newRiskControlReviewTaskControl.socPeriodEndDateMoment).format('MM/DD/YYYY');
    } else {
      vm.newRiskControlReviewTaskControl.socPeriodEndDate = null;      
    }


    if (vm.newRiskControlReviewTaskControl.insuranceEffectiveDateMoment !== undefined) {
      vm.newRiskControlReviewTaskControl.insuranceEffectiveDate = moment(vm.newRiskControlReviewTaskControl.insuranceEffectiveDateMoment).format('MM/DD/YYYY');
    } else {
      vm.newRiskControlReviewTaskControl.insuranceEffectiveDate = null;      
    }


    if (vm.newRiskControlReviewTaskControl.insuranceExpirationDateMoment !== undefined) {
      vm.newRiskControlReviewTaskControl.insuranceExpirationDate = moment(vm.newRiskControlReviewTaskControl.insuranceExpirationDateMoment).format('MM/DD/YYYY');
    } else {
      vm.newRiskControlReviewTaskControl.insuranceExpirationDate = null;      
    }


    loaderService.showViewLoader = true;
    return vmDataService.saveRiskControlReviewTaskControl(vm.newRiskControlReviewTaskControl).then(() => {
      const vmRiskControl = R.find(
        R.propEq(
          'riskControlReviewTaskControlId',
          vm.newRiskControlReviewTaskControl.riskControlReviewTaskControlId,
        ),
        vm.currentControlsReviewTask.riskControls,
      );

      vm.newRiskControlReviewTaskControl.hasUnsavedChanges = false;
      vm.newRiskControlReviewTaskControl.isComplete = isComplete;
      _setRiskControlStatus(vm.newRiskControlReviewTaskControl);
      angular.extend(vmRiskControl, vm.newRiskControlReviewTaskControl);
    }).finally(() => {
      loaderService.showViewLoader = false;
      $rootScope.$broadcast('refreshVMVendor', { vendorId: vm.vendor.vendorId });
    });
  }

  function _unlockRiskControl() {
    loaderService.showViewLoader = true;
    return vmDataService.markReviewTaskControlIncomplete(vm.newRiskControlReviewTaskControl.riskControlReviewTaskControlId).then(() => {
      const vmRiskControl = R.find(
        R.propEq(
          'riskControlReviewTaskControlId',
          vm.newRiskControlReviewTaskControl.riskControlReviewTaskControlId,
        ),
        vm.currentControlsReviewTask.riskControls,
      );

      vm.newRiskControlReviewTaskControl.hasUnsavedChanges = false;
      vm.newRiskControlReviewTaskControl.isComplete = false;
      _setRiskControlStatus(vm.newRiskControlReviewTaskControl);
      angular.extend(vmRiskControl, vm.newRiskControlReviewTaskControl);
    }).finally(() => {
      loaderService.showViewLoader = false;
      $rootScope.$broadcast('refreshVMVendor', { vendorId: vm.vendor.vendorId });
    });
  }

  function _assembleAnswers() {
    let answers = [];
    if (
      vm.newRiskControlReviewTaskControl.childQuestions
      && vm.newRiskControlReviewTaskControl.childQuestions.length > 0
    ) {
      answers = R.append(
        vm.newRiskControlReviewTaskControl.parentQuestion[0],
        vm.newRiskControlReviewTaskControl.childQuestions,
      );
    } else {
      answers = vm.newRiskControlReviewTaskControl.parentQuestion;
    }
    answers = R.map(
      (answer) => {
        answer.riskControlReviewTaskControlId = vm.newRiskControlReviewTaskControl.riskControlReviewTaskControlId;
        return answer;
      },
      answers,
    );
    return answers;
  }

  /** *******************************************************
   * Dialog Management
   */

  // function _showThisDialog() {
  //   return ssDialogService.show(ssDialogService.dialogs.vendorManagement.performControlsReviewDialog);
  // }

  function _showConfirmRevertRiskControlChangesDialog($event, handleYesClick) {
    ssDialogService.dialogs.vendorManagement.confirmRevertRiskControlChangesDialog = ssDialogService.alert(
      confirmDialog.getOptions($event, {
        dialogTitle: 'Revert Changes to Risk Control?',
        message: 'There are changes to the current Risk Control that have yet to be saved. '
          + 'Are you sure you want to revert these changes?',
        handleYesClick,
        handleNoClick: _closeRevertRiskControlChangesDialog,
      }),
    );
    return ssDialogService.show(
      ssDialogService.dialogs.vendorManagement.confirmRevertRiskControlChangesDialog,
    );
  }

  function _showConfirmDetachDocumentDialog($event, document) {
    ssDialogService.dialogs.vendorManagement.confirmDetachDocumentDialog = ssDialogService.alert(
      confirmDialog.getOptions($event, {
        dialogTitle: 'Remove Attached Document from Risk Control?',
        message: `Are you sure you want to detach the document "${document.displayName}" from the Control Review?`,
        handleYesClick() {
          _handleConfirmDetachDocument($event, document.riskControlReviewTaskControlDocumentId);
        },
        handleNoClick: _closeDetachDocumentDialog,
      }),
    );
    ssDialogService.show(ssDialogService.dialogs.vendorManagement.confirmDetachDocumentDialog);
  }

  function _showSelectRiskControlsForReviewDialog($event) {
    ssDialogService.dialogs.vendorManagement.selectRiskControlsForReviewDialog = ssDialogService.alert(
      vm.selectRiskControlsForReviewDialog.getOptions($event, {
        riskControlReviewTaskId: vm.currentControlsReviewTask.riskControlReviewTaskId,
        product: vm.product,
        vendor: vm.vendor,
      }),
    );
    return ssDialogService.show(ssDialogService.dialogs.vendorManagement.selectRiskControlsForReviewDialog);
  }

  function _closeDetachDocumentDialog() {
    return ssDialogService.hide(ssDialogService.dialogs.vendorManagement.confirmDetachDocumentDialog);
  }

  function _closeRevertRiskControlChangesDialog() {
    return ssDialogService.hide(
      ssDialogService.dialogs.vendorManagement.confirmRevertRiskControlChangesDialog,
    );
  }

  function _closeDialog() {
    return ssDialogService.hide(ssDialogService.dialogs.vendorManagement.performControlsReviewDialog);
  }

  function _setDatesForControls() {
    angular.forEach(vm.currentControlsReviewTask.riskControls, (currentControl) => {
      if (currentControl.socPeriodStartDate !== null) {
        currentControl.socPeriodStartDateMoment = moment(currentControl.socPeriodStartDate);
      }

      if (currentControl.socPeriodEndDate !== null) {
        currentControl.socPeriodEndDateMoment = moment(currentControl.socPeriodEndDate);
      }

      if (currentControl.insuranceEffectiveDate !== null) {
        currentControl.insuranceEffectiveDateMoment = moment(currentControl.insuranceEffectiveDate);
      }

      if (currentControl.insuranceExpirationDate !== null) {
        currentControl.insuranceExpirationDateMoment = moment(currentControl.insuranceExpirationDate);
      }
    });
  }
}

PerformControlsReviewDialogController.$inject = [
  '$q',
  '$rootScope',
  '$scope',
  'alerts',
  'confirmDialog',
  'loaderService',
  'ssDialogService',
  'vmDataService',
];
